import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  Brain, Wallet, TrendingUp, AlertTriangle, ArrowRightLeft, ArrowUpCircle, Percent, Info,
} from "lucide-react";
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from "recharts";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { fmtCurrency, fmtPct } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const PIE_COLORS = ["#10B981", "#F59E0B", "#3B82F6", "#EF4444", "#8B5CF6", "#EC4899"];
const REC_META = {
  sell_reallocate: { icon: ArrowRightLeft, color: "hsl(var(--destructive))" },
  increase_rent: { icon: ArrowUpCircle, color: "hsl(var(--warning))" },
  renegotiate_loan: { icon: Percent, color: "hsl(var(--warning))" },
  general: { icon: Info, color: "hsl(var(--muted-foreground))" },
};

export default function PortfolioOptimizer() {
  const { t, lang } = useI18n();
  const { data, isLoading } = useQuery({
    queryKey: ["portfolio-overview", lang],
    queryFn: async () => (await api.get("/portfolio/overview", { params: { lang } })).data,
  });

  if (isLoading || !data) return <div className="h-64 flex items-center justify-center text-muted-foreground">…</div>;

  const { summary, properties, subperformers, type_distribution, region_distribution, recommendations, tax_strategy_narrative } = data;

  return (
    <div className="space-y-6" data-testid="portfolio-optimizer-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-sm bg-secondary flex items-center justify-center">
          <Brain className="h-5 w-5 text-[hsl(var(--success))]" />
        </div>
        <div>
          <h1 className="font-display text-4xl">{t("portfolio_optimizer_title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("portfolio_optimizer_sub")}</p>
        </div>
      </div>

      {properties.length === 0 ? (
        <Card className="p-16 rounded-sm border-dashed border-border text-center">
          <Wallet className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">{t("quick_add")}</p>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              [Wallet, t("total_invested"), fmtCurrency(summary.total_invested), "#10B981"],
              [TrendingUp, t("total_monthly_income"), fmtCurrency(summary.total_monthly_income), "#3B82F6"],
              [Wallet, t("total_monthly_cashflow"), fmtCurrency(summary.total_monthly_cashflow), "#F59E0B"],
              [Percent, t("portfolio_gross_yield"), fmtPct(summary.portfolio_gross_yield), "#8B5CF6"],
            ].map(([Icon, label, value, accent], i) => (
              <motion.div key={label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                <Card className="p-6 rounded-sm border-border">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
                    <Icon className="h-4 w-4" style={{ color: accent }} />
                  </div>
                  <p className="font-display text-2xl tabular-nums">{value}</p>
                </Card>
              </motion.div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground -mt-2">
            {t("national_avg_gross_yield")}: {fmtPct(summary.national_avg_gross_yield)}
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="p-6 rounded-sm border-border">
              <h3 className="text-sm uppercase tracking-wider text-muted-foreground mb-6">{t("distribution_by_type")}</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={type_distribution}>
                  <XAxis dataKey="type" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} width={30} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 4, fontSize: 12 }} />
                  <Bar dataKey="count" fill="#10B981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
            <Card className="p-6 rounded-sm border-border">
              <h3 className="text-sm uppercase tracking-wider text-muted-foreground mb-6">{t("distribution_by_region")}</h3>
              {region_distribution.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-20">{t("no_region_data")}</p>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={region_distribution} dataKey="count" nameKey="region" cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={3}>
                      {region_distribution.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 4, fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Card>
          </div>

          <Card className="rounded-sm border-border overflow-hidden" data-testid="portfolio-properties-table">
            <div className="px-6 py-4 border-b border-border">
              <h3 className="text-sm uppercase tracking-wider text-muted-foreground">{t("properties_by_performance")}</h3>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("name")}</TableHead>
                  <TableHead className="text-right">{t("gross_yield")}</TableHead>
                  <TableHead className="text-right">{t("monthly_cashflow")}</TableHead>
                  <TableHead className="text-right">{t("vs_market")}</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {properties.map((p) => (
                  <TableRow key={p.id} data-testid={`portfolio-property-${p.id}`}>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtPct(p.gross_yield)}</TableCell>
                    <TableCell className={`text-right tabular-nums ${p.monthly_cashflow < 0 ? "text-[hsl(var(--destructive))]" : ""}`}>{fmtCurrency(p.monthly_cashflow)}</TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">{p.market_gross_yield ? fmtPct(p.market_gross_yield) : "—"}</TableCell>
                    <TableCell>{p.is_subperformer && <Badge variant="outline" className="rounded-sm text-[hsl(var(--warning))] border-[hsl(var(--warning))]">{t("subperformer")}</Badge>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          {recommendations.length > 0 && (
            <Card className="rounded-sm border-border overflow-hidden" data-testid="portfolio-recommendations">
              <div className="px-6 py-4 border-b border-border">
                <h3 className="text-sm uppercase tracking-wider text-muted-foreground">{t("ai_recommendations")}</h3>
              </div>
              <div className="divide-y divide-border">
                {recommendations.map((r, i) => {
                  const meta = REC_META[r.type] || REC_META.general;
                  const Icon = meta.icon;
                  const prop = properties.find((p) => p.id === r.property_id);
                  return (
                    <motion.div key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.05 }}
                      className="flex items-start gap-3 px-6 py-4">
                      <Icon className="h-4 w-4 mt-0.5 shrink-0" style={{ color: meta.color }} />
                      <div>
                        <p className="text-sm">{r.text}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {prop ? prop.name : t("general_recommendation")} · {r.rationale}
                        </p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </Card>
          )}

          {tax_strategy_narrative && (
            <Card className="p-6 rounded-sm border-border" data-testid="portfolio-tax-narrative">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="h-4 w-4 text-[hsl(var(--warning))]" />
                <h3 className="text-sm uppercase tracking-wider text-muted-foreground">{t("tax_strategy_narrative")}</h3>
              </div>
              <p className="text-sm leading-relaxed">{tax_strategy_narrative}</p>
              <p className="text-xs text-muted-foreground mt-3">{t("tax_disclaimer")}</p>
              {data.ai_generated === false && (
                <p className="text-xs text-muted-foreground mt-1" data-testid="portfolio-ai-fallback-note">{t("ai_fallback_note")}</p>
              )}
            </Card>
          )}
        </>
      )}
    </div>
  );
}
